/**
 * Web Audio Capture - Browser implementation
 * Dual recorder: buffer-window chunks + 30s milestone
 */

import type { IAudioCapture, AudioChunkCallback } from '@alphahub/eie-core';
// @ts-ignore - raw import for Web Worker inline
import lamejsSource from './vendor/lamejs.iife.js?raw';

let MediaRecorderPolyfill: any;
let encoderRegistered = false;

const initializeEncoder = async () => {
  if (encoderRegistered) return;
  try {
    const { MediaRecorder: ExtendableMediaRecorder, register } = await import('extendable-media-recorder');
    const { connect } = await import('extendable-media-recorder-wav-encoder');
    await register(await connect());
    MediaRecorderPolyfill = ExtendableMediaRecorder;
    encoderRegistered = true;
    console.log('✅ WAV encoder registered');
  } catch {
    console.warn('⚠️ WAV encoder not available, using native MediaRecorder');
    MediaRecorderPolyfill = MediaRecorder;
  }
};

export class WebAudioCapture implements IAudioCapture {
  private isRecording: boolean = false;
  private chunkRecorder?: MediaRecorder;
  private milestoneRecorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunkCallback?: AudioChunkCallback;
  private chunkCount: number = 0;
  private chunkDuration: number;
  private milestoneDuration: number;
  private chunkBuffer: number;
  private milestoneCount: number = 0;
  private timerChunk?: number;
  private timerMilestone?: number;

  constructor(config?: { chunkDuration?: number; milestoneDuration?: number; chunkBuffer?: number }) {
    this.chunkDuration = config?.chunkDuration ?? 5000;
    this.milestoneDuration = config?.milestoneDuration ?? 30000;
    this.chunkBuffer = config?.chunkBuffer ?? 1;
  }

  async start(): Promise<void> {
    if (this.isRecording) throw new Error('Audio capture already running');

    await initializeEncoder();
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 48000 },
    });

    try {
      await this.startChunkRecorder();
      await this.startMilestoneRecorder();
      this.isRecording = true;
      console.log('✅ Dual audio recording started');
    } catch (err) {
      this.releaseStream();
      throw err;
    }
  }

  stop(): void {
    if (!this.isRecording) return;
    console.log('🛑 Stopping audio capture...');

    if (this.chunkRecorder?.state === 'recording') this.chunkRecorder.stop();
    if (this.timerChunk) clearTimeout(this.timerChunk);
    if (this.milestoneRecorder?.state === 'recording') this.milestoneRecorder.stop();
    if (this.timerMilestone) clearTimeout(this.timerMilestone);

    this.releaseStream();
    if (this.mp3Worker) { this.mp3Worker.terminate(); this.mp3Worker = undefined; }
    this.isRecording = false;
    this.chunkCount = 0;
    this.milestoneCount = 0;
  }

  /** Force-release microphone — call this if a crash left the mic stuck */
  forceRelease(): void {
    console.log('🔓 Force-releasing microphone...');
    try { if (this.chunkRecorder?.state === 'recording') this.chunkRecorder.stop(); } catch {}
    try { if (this.milestoneRecorder?.state === 'recording') this.milestoneRecorder.stop(); } catch {}
    if (this.timerChunk) clearTimeout(this.timerChunk);
    if (this.timerMilestone) clearTimeout(this.timerMilestone);
    this.releaseStream();
    if (this.mp3Worker) { this.mp3Worker.terminate(); this.mp3Worker = undefined; }
    this.isRecording = false;
    this.chunkCount = 0;
    this.milestoneCount = 0;
  }

  private releaseStream(): void {
    // Don't call track.stop() — on USB combo devices it kills the
    // hardware and prevents future getUserMedia from working.
    // Just drop the reference; GC + page unload will clean up.
    this.stream = undefined;
  }

  onChunkReady(callback: AudioChunkCallback): void {
    this.chunkCallback = callback;
  }

  getIsRecording(): boolean {
    return this.isRecording;
  }

  private async startChunkRecorder(): Promise<void> {
    if (!this.stream || !MediaRecorderPolyfill) return;

    const rec = new MediaRecorderPolyfill(this.stream, { mimeType: 'audio/wav' });
    this.chunkRecorder = rec;

    rec.ondataavailable = async (event: BlobEvent) => {
      const chunkTimestamp = Date.now();
      this.chunkCount += this.chunkBuffer;
      const chunkId = this.chunkCount;

      // Restart recorder immediately (before async processing)
      if (this.isRecording) {
        try { rec.start(); } catch { /* already started */ }
      }

      if (event.data.size > 0) {
        console.log(`🎤 Chunk audio blob: ${event.data.size} bytes, chunkId: ${chunkId}`);
        const isSilent = await this.detectSilence(event.data);
        if (!this.isRecording) return;
        const cb = this.chunkCallback;
        if (!cb) return;

        if (isSilent) {
          console.log(`🔇 Chunk ${chunkId}: silent`);
          cb({ base64Audio: null, timestamp: chunkTimestamp, chunkId, isMilestone: false });
        } else {
          console.log(`🔊 Chunk ${chunkId}: has audio, converting to mp3...`);
          const mp3Blob = await this.convertWavToMp3(event.data);
          if (!this.isRecording) return;
          const base64Audio = await this.blobToBase64(mp3Blob);
          if (!this.isRecording) return;
          console.log(`✅ Chunk ${chunkId}: mp3 ready, ${base64Audio.length} chars`);
          cb({ base64Audio, timestamp: chunkTimestamp, chunkId, isMilestone: false });
        }
      }
    };

    rec.start();
    this.scheduleChunkStop();
  }

  private async startMilestoneRecorder(): Promise<void> {
    if (!this.stream || !MediaRecorderPolyfill) return;

    const rec = new MediaRecorderPolyfill(this.stream, { mimeType: 'audio/wav' });
    this.milestoneRecorder = rec;

    rec.ondataavailable = async (event: BlobEvent) => {
      const chunkTimestamp = Date.now();
      this.milestoneCount++;
      const milestoneChunkId = this.milestoneCount * (this.milestoneDuration / this.chunkDuration);

      // Restart recorder immediately (before async processing)
      if (this.isRecording) {
        try { rec.start(); } catch { /* already started */ }
      }

      if (event.data.size > 0) {
        console.log(`🎤 Milestone audio blob: ${event.data.size} bytes, chunkId: ${milestoneChunkId}`);
        const isSilent = await this.detectSilence(event.data);
        if (!this.isRecording) return;
        const cb = this.chunkCallback;
        if (!cb) return;

        if (isSilent) {
          console.log(`🔇 Milestone ${milestoneChunkId}: silent`);
          cb({ base64Audio: null, timestamp: chunkTimestamp, chunkId: milestoneChunkId, isMilestone: true });
        } else {
          console.log(`🔊 Milestone ${milestoneChunkId}: has audio, converting to mp3...`);
          const mp3Blob = await this.convertWavToMp3(event.data);
          if (!this.isRecording) return;
          const base64Audio = await this.blobToBase64(mp3Blob);
          if (!this.isRecording) return;
          console.log(`✅ Milestone ${milestoneChunkId}: mp3 ready, ${base64Audio.length} chars`);
          cb({ base64Audio, timestamp: chunkTimestamp, chunkId: milestoneChunkId, isMilestone: true });
        }
      }
    };

    rec.start();
    this.scheduleMilestoneStop();
  }

  private scheduleChunkStop(): void {
    const windowDuration = this.chunkBuffer * this.chunkDuration;
    this.timerChunk = window.setTimeout(() => {
      if (this.chunkRecorder?.state === 'recording') {
        this.chunkRecorder.stop();
        this.scheduleChunkStop();
      }
    }, windowDuration);
  }

  private scheduleMilestoneStop(): void {
    this.timerMilestone = window.setTimeout(() => {
      if (this.milestoneRecorder?.state === 'recording') {
        this.milestoneRecorder.stop();
        this.scheduleMilestoneStop();
      }
    }, this.milestoneDuration);
  }

  private async detectSilence(audioBlob: Blob): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function () {
        const arrayBuffer = this.result as ArrayBuffer;
        const dataView = new DataView(arrayBuffer);
        let dataOffset = 12;
        while (dataOffset < arrayBuffer.byteLength - 8) {
          const chunkId = String.fromCharCode(
            dataView.getUint8(dataOffset), dataView.getUint8(dataOffset + 1),
            dataView.getUint8(dataOffset + 2), dataView.getUint8(dataOffset + 3)
          );
          const chunkSize = dataView.getUint32(dataOffset + 4, true);
          if (chunkId === 'data') { dataOffset += 8; break; }
          dataOffset += 8 + chunkSize;
        }
        const samples = new Int16Array(arrayBuffer, dataOffset);
        let sum = 0;
        for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
        const rms = Math.sqrt(sum / samples.length) / 32768;
        resolve(rms < 0.002);
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(audioBlob);
    });
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private mp3Worker?: Worker;
  private mp3JobId = 0;
  private mp3Callbacks = new Map<number, { resolve: (b: Blob) => void; reject: (e: any) => void }>();

  private getMp3Worker(): Worker {
    if (this.mp3Worker) return this.mp3Worker;

    const workerCode = `
      ${lamejsSource}
      self.onmessage = function(e) {
        var id = e.data.id;
        var buf = e.data.buf;
        var view = new DataView(buf);
        var channels = view.getUint16(22, true);
        var sampleRate = view.getUint32(24, true);
        var offset = 12;
        while (offset < buf.byteLength - 8) {
          var cid = String.fromCharCode(view.getUint8(offset), view.getUint8(offset+1), view.getUint8(offset+2), view.getUint8(offset+3));
          var size = view.getUint32(offset+4, true);
          if (cid === 'data') { offset += 8; break; }
          offset += 8 + size;
        }
        var samples = new Int16Array(buf, offset);
        var encoder = new lamejs.Mp3Encoder(channels, sampleRate, 128);
        var mp3 = encoder.encodeBuffer(samples);
        var flush = encoder.flush();
        var combined = new Uint8Array(mp3.length + flush.length);
        combined.set(mp3, 0);
        combined.set(flush, mp3.length);
        self.postMessage({ id: id, buf: combined.buffer }, [combined.buffer]);
      };
    `;
    this.mp3Worker = new Worker(URL.createObjectURL(new Blob([workerCode], { type: 'application/javascript' })));
    this.mp3Worker.onmessage = (e: MessageEvent) => {
      const { id, buf } = e.data;
      const cb = this.mp3Callbacks.get(id);
      if (cb) {
        this.mp3Callbacks.delete(id);
        cb.resolve(new Blob([buf], { type: 'audio/mp3' }));
      }
    };
    this.mp3Worker.onerror = (e) => {
      this.mp3Callbacks.forEach(cb => cb.reject(e));
      this.mp3Callbacks.clear();
    };
    return this.mp3Worker;
  }

  private async convertWavToMp3(wavBlob: Blob): Promise<Blob> {
    const arrayBuffer = await wavBlob.arrayBuffer();
    const worker = this.getMp3Worker();
    const id = ++this.mp3JobId;

    return new Promise((resolve, reject) => {
      this.mp3Callbacks.set(id, { resolve, reject });
      worker.postMessage({ id, buf: arrayBuffer }, [arrayBuffer]);
    });
  }
}
